// StudentViewCaseStudy.jsx - mirrors StudentViewPdf.jsx, breadcrumb points back to Case Studies
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';

const WORKER_SOURCES = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js',
  'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js'
];

pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[0];

function StudentViewCaseStudy() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const canvasRef = useRef(null);
  const { bookTitle, pdfUrl, courseName } = location.state || {};
  const pdfDocumentRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const initWorker = async () => {
      for (const src of WORKER_SOURCES) {
        try {
          pdfjsLib.GlobalWorkerOptions.workerSrc = src;
          await new Promise(resolve => setTimeout(resolve, 500));
          const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array([37, 80, 68, 70, 45, 49, 46, 10]).buffer });
          await new Promise((resolve, reject) => {
            loadingTask.promise.then(
              () => reject(new Error('Should not succeed')),
              (err) => {
                if (err.message.includes('Invalid PDF') || err.message.includes('format')) {
                  resolve();
                } else {
                  reject(err);
                }
              }
            );
          });
          if (mounted) {
            setLoading(false);
          }
          return;
        } catch (err) {
          // try next worker source
        }
      }
      if (mounted) {
        setError('Failed to initialize PDF viewer. Please try refreshing the page.');
        setLoading(false);
      }
    };

    initWorker();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!bookTitle || !pdfUrl) {
      setError('Case study information not found. Please go back and try again.');
      setLoading(false);
      return;
    }
  }, [bookTitle, pdfUrl]);

  const loadPdf = async () => {
    try {
      setError('');
      const response = await fetch(pdfUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/pdf',
        }
      });
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to fetch PDF: ${response.status}`);
        } else {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
      }
      const arrayBuffer = await response.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      pdfDocumentRef.current = pdf;
      setPageCount(pdf.numPages);
      if (canvasRef.current) {
        renderPage(1, pdf);
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError(err.message || 'Failed to load PDF');
    }
  };

  const renderPage = async (pageNumber, pdf = pdfDocumentRef.current) => {
    try {
      const canvas = canvasRef.current;
      if (!pdf || !canvas) {
        return;
      }
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
      setError('Failed to render page: ' + err.message);
    }
  };

  useEffect(() => {
    if (!loading && !error && pdfUrl) {
      loadPdf();
    }
  }, [loading, pdfUrl]);

  useEffect(() => {
    if (pdfDocumentRef.current && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [currentPage, scale]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < pageCount) setCurrentPage(currentPage + 1);
  };
  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const handleRetry = () => window.location.reload();
  const handleOpenInNewTab = () => window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  const handleGoBack = () => navigate(-1);

  const securityFunctions = {
    disableRightClick: (e) => {
      e.preventDefault();
      return false;
    },
    disableKeyboardShortcuts: (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (['p', 's', 'P', 'S', 'c', 'C'].includes(e.key)) {
          e.preventDefault();
          return false;
        }
      }
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault();
        return false;
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', securityFunctions.disableKeyboardShortcuts);
    document.addEventListener('contextmenu', securityFunctions.disableRightClick);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.removeEventListener('keydown', securityFunctions.disableKeyboardShortcuts);
      document.removeEventListener('contextmenu', securityFunctions.disableRightClick);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, []);

  if (!bookTitle || !pdfUrl) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body">
            <div className="alert alert-danger m-4">
              <h4>Error</h4>
              <p>Case study not found. Please go back and try again.</p>
              <button onClick={handleGoBack} className="btn btn-primary">Go Back</button>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb mb-4">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/dashboard" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Home</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><Link to="/student-case-studies" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Case Studies</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">View PDF</span></li>
            </ul>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h4 className="mb-0">{bookTitle}</h4>
                  {courseName && <p className="text-muted mb-0">Course: {courseName}</p>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!loading && !error && pageCount > 0 && (
                    <span className="badge bg-success">Page {currentPage} of {pageCount}</span>
                  )}
                  <div className="btn-group">
                    <button
                      onClick={handleZoomOut}
                      className="btn btn-sm btn-outline-secondary"
                      disabled={loading || error || scale <= 0.5}
                      title="Zoom Out"
                    >
                      <i className="ph ph-magnifying-glass-minus"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" disabled>
                      {Math.round(scale * 100)}%
                    </button>
                    <button
                      onClick={handleZoomIn}
                      className="btn btn-sm btn-outline-secondary"
                      disabled={loading || error || scale >= 3.0}
                      title="Zoom In"
                    >
                      <i className="ph ph-magnifying-glass-plus"></i>
                    </button>
                  </div>
                  <button
                    onClick={handleOpenInNewTab}
                    className="btn btn-sm btn-info"
                    title="Open in new tab"
                  >
                    <i className="ph ph-arrow-square-out"></i>
                  </button>
                </div>
              </div>
            </div>

            <div
              className="card-body p-0 text-center"
              style={{
                height: '70vh',
                overflow: 'auto',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
              }}
              onContextMenu={securityFunctions.disableRightClick}
              onKeyDown={securityFunctions.disableKeyboardShortcuts}
              tabIndex="0"
            >
              {error && (
                <div className="alert alert-danger m-4 w-100">
                  <h5>Error Loading PDF</h5>
                  <p>{error}</p>
                  <div className="mt-3">
                    <button onClick={handleRetry} className="btn btn-primary me-2">
                      <i className="ph ph-arrow-clockwise me-2"></i> Retry
                    </button>
                    <button onClick={handleOpenInNewTab} className="btn btn-info me-2">
                      <i className="ph ph-arrow-square-out me-2"></i> Open in New Tab
                    </button>
                    <button onClick={handleGoBack} className="btn btn-secondary">
                      <i className="ph ph-arrow-left me-2"></i> Go Back
                    </button>
                  </div>
                </div>
              )}

              {loading && !error && (
                <div className="text-center p-5 w-100">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading PDF...</span>
                  </div>
                  <p className="mt-3">Loading PDF viewer...</p>
                </div>
              )}

              {!loading && !error && (
                <div style={{ padding: '20px', minWidth: '100%' }}>
                  <canvas
                    ref={canvasRef}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                </div>
              )}
            </div>

            <div className="card-footer">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <small className="text-muted">
                  <i className="ph ph-file-pdf me-1"></i>
                  {bookTitle}
                </small>
                {!loading && !error && pageCount > 0 && (
                  <div className="d-flex gap-2">
                    <button
                      onClick={handlePrevPage}
                      className="btn btn-sm btn-primary"
                      disabled={currentPage <= 1}
                    >
                      <i className="ph ph-caret-left me-1"></i> Previous
                    </button>
                    <span className="btn btn-sm btn-outline-secondary disabled">
                      {currentPage} / {pageCount}
                    </span>
                    <button
                      onClick={handleNextPage}
                      className="btn btn-sm btn-primary"
                      disabled={currentPage >= pageCount}
                    >
                      Next <i className="ph ph-caret-right ms-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default StudentViewCaseStudy;
